import {engineContext} from './context.js';

/* ZooInvest Portfolio & Re-entry Engine v0.1.0-alpha
 * Engine-only extension. UI intentionally unchanged.
 * All thresholds/weights are PROVISIONAL / UNCALIBRATED.
 * Decision support only: no automatic buy/sell instructions.
 */
(function(root, factory){
  const api = factory(root && root.ZooInvestEngine ? root.ZooInvestEngine : null);
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.ZooInvestPortfolioEngine = api;
})(engineContext, function(Core){
  'use strict';

  const VERSION = '0.1.0-alpha';
  const CALIBRATED = false;
  const ACTIONS = Object.freeze({
    AVERAGE_DOWN:'AVERAGE_DOWN', CHASE:'CHASE', SELL:'SELL',
    HOLD_THROUGH_EARNINGS:'HOLD_THROUGH_EARNINGS', ADD:'ADD', TRIM:'TRIM'
  });
  const CAUSES = Object.freeze({
    COMPANY:'COMPANY', SECTOR:'SECTOR', MARKET:'MARKET', EVENT:'EVENT',
    EXPECTATIONS:'EXPECTATIONS', VALUATION:'VALUATION', UNKNOWN:'UNKNOWN'
  });
  const SLEEVES = Object.freeze({CORE:'CORE', TACTICAL:'TACTICAL', SATELLITE:'SATELLITE'});

  const CONFIG = deepFreeze({
    calibrated:false,
    capitalCompetition:{riskPenalty:0.35, reviewGap:1.0},
    exitRisk:{weights:{overvaluation:1.0,sectorOutflow:0.9,momentum:0.7,expectations:1.0,event:0.8,thesisDamage:1.2}},
    causeOfMove:{minSignal:4.0, dominanceGap:0.8},
    maxSinglePositionPct:20,
    thesisPainFloor:4.0
  });

  function deepFreeze(v){
    if(v && typeof v==='object' && !Object.isFrozen(v)){
      Object.freeze(v); Object.values(v).forEach(deepFreeze);
    }
    return v;
  }
  function num(v){ return typeof v==='number' && Number.isFinite(v); }
  function clamp(v,lo=0,hi=10){ return Math.max(lo,Math.min(hi,v)); }
  function round(v,d=2){ return num(v) ? Number(v.toFixed(d)) : null; }
  function meanWeighted(rows){
    const valid=rows.filter(r=>num(r.value)&&num(r.weight)&&r.weight>0);
    if(!valid.length) return null;
    const sw=valid.reduce((a,r)=>a+r.weight,0);
    return round(valid.reduce((a,r)=>a+r.value*r.weight,0)/sw);
  }
  function result(engine, payload){ return deepFreeze({engine,version:VERSION,calibrated:false,...payload}); }
  function normState(x){ return String(x||'').trim().toUpperCase(); }
  function gatePass(g){ return !!g && normState(g.state)==='PASS'; }
  function gatePartial(g){ return !!g && ['PARTIAL','OPEN'].includes(normState(g.state)); }
  function evidenceStrong(s){ return ['VERIFIED','PARTIAL'].includes(normState(s)); }

  function decisionProtection(action, context={}){
    const a=normState(action);
    const allowed=Object.values(ACTIONS);
    if(!allowed.includes(a)) return result('DecisionProtection',{state:'OPEN',action:a||null,questions:[],checks:{},blockers:['unknown_action'],note:'Unknown action; no decision gate produced.'});

    const common=[
      'What changed in the business or thesis?',
      'Is this decision driven by new evidence or by price emotion?',
      'What evidence would prove this decision wrong?',
      'What is the best alternative use of this capital?'
    ];
    const specific={
      AVERAGE_DOWN:['Why did the price fall?','Is the thesis intact or repaired?','Would this pass a fresh Entry Gate today?'],
      CHASE:['What new evidence justifies paying more now?','Has the setup become extended?','Is the catalyst verified and still ahead?'],
      SELL:['Is the thesis broken, or is only the price painful?','Are fundamentals/regime improving enough to invalidate the old target?','What replaces this capital?'],
      HOLD_THROUGH_EARNINGS:['What is the event risk?','How demanding are expectations?','What downside is acceptable if the event disappoints?'],
      ADD:['What improved since the original entry?','Does the fresh Entry Gate still pass?','Does concentration remain acceptable?'],
      TRIM:['Is the reason concentration/risk or merely recent price movement?','What thesis exposure remains after trimming?','Is there a better capital competitor?']
    }[a];

    // Purchase price / break-even are intentionally excluded from gating logic.
    const blockers=[]; const flags=[];
    const thesis=normState(context.thesisState);
    const cause=normState(context.causeOfMove?.cause || context.causeOfMove);
    const freshGate=context.freshEntryGate || null;
    const reason=normState(context.reason);
    const anchoringReason=['BREAKEVEN','BREAK_EVEN','PURCHASE_PRICE','GET_BACK_TO_BREAKEVEN','LOSS_AVERSION'].includes(reason);

    if(anchoringReason) flags.push('purchase_price_anchoring_ignored');
    if(['AVERAGE_DOWN','ADD'].includes(a)){
      if(['BROKEN','INVALIDATED'].includes(thesis)) blockers.push('thesis_not_intact');
      if(!cause || cause==='UNKNOWN') blockers.push('cause_of_move_unknown');
      if(!gatePass(freshGate)) blockers.push('fresh_entry_gate_not_passed');
    }
    if(a==='CHASE'){
      if(context.extended===true) flags.push('setup_extended');
      if(context.catalystVerified===false) blockers.push('catalyst_unverified');
    }
    if(a==='SELL'){
      if(anchoringReason && !context.thesisBreaker && !context.riskEscalation) blockers.push('sell_reason_is_anchoring_only');
      if(!thesis) flags.push('thesis_state_missing');
    }
    if(a==='HOLD_THROUGH_EARNINGS'){
      if(!num(context.eventRisk)) blockers.push('event_risk_missing');
      if(!num(context.expectationRisk)) blockers.push('expectation_risk_missing');
      if(context.verificationStatus && !evidenceStrong(context.verificationStatus)) blockers.push('critical_event_evidence_unverified');
    }
    if(a==='TRIM' && !num(context.positionWeightPct)) flags.push('position_weight_missing');

    const state=blockers.length?'BLOCKED':flags.length?'REVIEW':'READY_FOR_DECISION';
    return result('DecisionProtection',{action:a,state,questions:[...common,...specific],checks:{thesisState:thesis||null,causeOfMove:cause||null,freshEntryGate:freshGate?normState(freshGate.state):null},blockers,flags,note:'Purchase price and break-even anchoring are not decision inputs.'});
  }

  function appendDecisionJournal(history=[], entry={}){
    const prior=Array.isArray(history)?history:[];
    const e=deepFreeze({
      id:entry.id || `decision-${prior.length+1}`,
      timestamp:entry.timestamp || new Date().toISOString(),
      ticker:entry.ticker || null,
      action:entry.action || null,
      thesisSnapshot:entry.thesisSnapshot || null,
      expectedCatalysts:Array.isArray(entry.expectedCatalysts)?[...entry.expectedCatalysts]:[],
      invalidationConditions:Array.isArray(entry.invalidationConditions)?[...entry.invalidationConditions]:[],
      outcome:entry.outcome || null,
      learnedRule:entry.learnedRule || null,
      modelVersion:entry.modelVersion || VERSION
    });
    return deepFreeze([...prior.map(x=>deepFreeze({...x})),e]);
  }

  function makeSaleRecord(data={}){
    return deepFreeze({
      id:data.id || `sale-${data.ticker||'unknown'}-${data.date||'undated'}`,
      ticker:data.ticker||null,
      date:data.date||null,
      salePrice:num(data.salePrice)?data.salePrice:null,
      reason:data.reason||null,
      thesisStateAtSale:data.thesisStateAtSale||null,
      causeAtSale:data.causeAtSale||null,
      modelVersion:data.modelVersion||VERSION,
      immutable:true
    });
  }

  function thesisCard(input={}){
    const breakers=Array.isArray(input.breakConditions)?input.breakConditions:[];
    const must=Array.isArray(input.mustHappen)?input.mustHappen:[];
    const triggered=breakers.filter(x=>x && x.triggered===true);
    const unmet=must.filter(x=>x && x.met===false);
    let state=normState(input.currentState)||'OPEN';
    if(triggered.length) state='BROKEN';
    else if(unmet.length && state==='OPEN') state='AT_RISK';
    else if(!triggered.length && !unmet.length && input.whyOwned) state=state==='OPEN'?'INTACT':state;
    return result('ThesisCard',{ticker:input.ticker||null,state,whyOwned:input.whyOwned||null,mustHappen:must,breakConditions:breakers,triggeredBreakers:triggered,catalysts:Array.isArray(input.catalysts)?input.catalysts:[],note:'Thesis state is separate from price P/L.'});
  }

  function comparisonScore(x={}, cfg=CONFIG.capitalCompetition){
    if(num(x.comparisonScore)) return round(x.comparisonScore);
    if(!num(x.opportunityScore)) return null;
    const risk=num(x.riskScore)?x.riskScore:5;
    return round(clamp(x.opportunityScore)-cfg.riskPenalty*clamp(risk));
  }

  function capitalCompetition(holding={}, alternatives=[], cfg=CONFIG.capitalCompetition){
    const current=comparisonScore(holding,cfg);
    const alts=(Array.isArray(alternatives)?alternatives:[]).map(x=>({...x,comparisonScore:comparisonScore(x,cfg)})).filter(x=>num(x.comparisonScore)).sort((a,b)=>b.comparisonScore-a.comparisonScore);
    if(!num(current)) return result('CapitalCompetition',{state:'OPEN',holding:holding.ticker||null,currentScore:null,bestAlternative:alts[0]||null,opportunityCostGap:null,note:'Current holding lacks comparable opportunity inputs.'});
    const best=alts[0]||null;
    const gap=best?round(best.comparisonScore-current):null;
    return result('CapitalCompetition',{state:best&&gap>=cfg.reviewGap?'REVIEW':'COMPETITIVE',holding:holding.ticker||null,currentScore:current,bestAlternative:best,opportunityCostGap:gap,reviewThreshold:cfg.reviewGap,note:'Review signal only; never an automatic rotation instruction.'});
  }

  function portfolioDrag(positions=[]){
    const rows=(Array.isArray(positions)?positions:[]).map(p=>{
      const w=num(p.weight)?p.weight:(num(p.weightPct)?p.weightPct/100:null);
      const ret=num(p.returnPct)?p.returnPct:null;
      const thesis=num(p.thesisHealth)?clamp(p.thesisHealth):null;
      return {ticker:p.ticker||null,weight:w,returnPct:ret,thesisHealth:thesis,
        priceContribution:num(w)&&num(ret)?round(w*ret):null,
        thesisPainContribution:num(w)&&num(thesis)?round(w*(10-thesis)):null};
    });
    const pricePain=round(rows.filter(r=>num(r.priceContribution)&&r.priceContribution<0).reduce((a,r)=>a+Math.abs(r.priceContribution),0));
    const thesisPain=round(rows.filter(r=>num(r.thesisPainContribution)).reduce((a,r)=>a+r.thesisPainContribution,0));
    return result('PortfolioDrag',{pricePain,thesisPain,priceDetractors:rows.filter(r=>num(r.priceContribution)&&r.priceContribution<0).sort((a,b)=>a.priceContribution-b.priceContribution),thesisDetractors:rows.filter(r=>num(r.thesisPainContribution)&&r.thesisPainContribution>0).sort((a,b)=>b.thesisPainContribution-a.thesisPainContribution),rows,note:'Price pain and thesis pain are intentionally not merged.'});
  }

  function exitScore(inputs={}, cfg=CONFIG.exitRisk){
    const map={overvaluation:'overvaluationRisk',sectorOutflow:'sectorOutflowRisk',momentum:'momentumRisk',expectations:'expectationRisk',event:'eventRisk',thesisDamage:'thesisDamageRisk'};
    const rows=Object.entries(cfg.weights).map(([k,w])=>({key:k,value:num(inputs[map[k]])?clamp(inputs[map[k]]):null,weight:w}));
    const risk=meanWeighted(rows);
    const state=!num(risk)?'OPEN':risk>=7.5?'HIGH':risk>=5.5?'ELEVATED':risk>=3.5?'WATCH':'LOW';
    return result('ExitScore',{risk,state,components:Object.fromEntries(rows.map(r=>[r.key,r.value])),note:'Risk of remaining; separate from Exit Challenge and not a sell instruction.'});
  }

  function exitChallenge(context={}){
    const flags=[]; const supports=[];
    const reason=normState(context.reason);
    if(['PURCHASE_PRICE','BREAKEVEN','BREAK_EVEN','LOSS_AVERSION'].includes(reason)) flags.push('anchoring_reason_ignored');
    if(['IMPROVING','ACCELERATING'].includes(normState(context.fundamentalsTrend))) supports.push('fundamentals_improving');
    if(['IMPROVING','RISK_ON','SUPPORTIVE'].includes(normState(context.regimeTrend))) supports.push('regime_improving');
    if(['BROKEN','INVALIDATED'].includes(normState(context.thesisState))) flags.push('thesis_broken');
    if(num(context.exitRisk)&&context.exitRisk>=7) flags.push('high_exit_risk');
    let state='REVIEW_EXIT';
    if(flags.length===1 && flags[0]==='anchoring_reason_ignored' && supports.length) state='CHALLENGE_OLD_TARGET';
    if(supports.length>=2 && !flags.includes('thesis_broken')) state='CHALLENGE_OLD_TARGET';
    return result('ExitChallenge',{state,flags,supports,questions:['Is the thesis broken or only the price painful?','Would you sell this company today if you had never owned it?','Are fundamentals or regime improving enough to invalidate the old target?','What will replace the capital?'],seedReentryOnExecutedSale:true,note:'If a sale is executed, seed Re-entry Watch; do not delete the original sale record.'});
  }

  function causeOfMove(signals={}, cfg=CONFIG.causeOfMove){
    const rows=[
      [CAUSES.COMPANY,signals.companyDamage], [CAUSES.SECTOR,signals.sectorShock], [CAUSES.MARKET,signals.marketShock],
      [CAUSES.EVENT,signals.eventShock], [CAUSES.EXPECTATIONS,signals.expectationsReset], [CAUSES.VALUATION,signals.valuationReset]
    ].filter(([,v])=>num(v)).map(([cause,v])=>({cause,severity:clamp(v)})).sort((a,b)=>b.severity-a.severity);
    if(!rows.length || rows[0].severity<cfg.minSignal) return result('CauseOfMove',{cause:CAUSES.UNKNOWN,severity:rows[0]?.severity??null,confidence:'LOW',ranked:rows,note:'Insufficient evidence to classify the move.'});
    const gap=rows.length>1?rows[0].severity-rows[1].severity:10;
    const confidence=gap>=cfg.dominanceGap?'HIGH':'MIXED';
    return result('CauseOfMove',{cause:rows[0].cause,severity:round(rows[0].severity),confidence,ranked:rows,note:'Company, sector, market, event and expectations/valuation causes remain distinct.'});
  }

  function seedReentryWatch(saleRecord, cause=null){
    if(!saleRecord) return null;
    return deepFreeze({id:`reentry-${saleRecord.id}`,ticker:saleRecord.ticker,saleRecord,createdAt:new Date().toISOString(),causeAtSeed:cause?.cause||cause||saleRecord.causeAtSale||CAUSES.UNKNOWN,status:'WATCH',modelVersion:VERSION});
  }

  function reentryGate(input={}){
    const sold=input.soldRecord||null;
    const cause=normState(input.causeOfMove?.cause || input.causeOfMove || CAUSES.UNKNOWN);
    const fresh=input.freshEntryGate||null;
    const thesis=normState(input.thesisState);
    const verification=normState(input.verificationStatus);
    const blockers=[]; const flags=[];
    if(!sold || sold.immutable!==true) blockers.push('immutable_sale_record_required');
    if(!cause || cause===CAUSES.UNKNOWN) blockers.push('cause_of_move_unknown');
    if(!gatePass(fresh)) blockers.push('fresh_entry_gate_not_passed');
    if(verification && !evidenceStrong(verification)) blockers.push('verification_too_weak');
    if(cause===CAUSES.COMPANY && !['REPAIRED','NEW_THESIS','INTACT_AFTER_REVIEW'].includes(thesis)) blockers.push('company_damage_requires_repaired_thesis');
    if(cause===CAUSES.EVENT && input.eventResolved!==true) flags.push('event_not_resolved');
    if([CAUSES.MARKET,CAUSES.SECTOR].includes(cause) && input.regimeImproved!==true) flags.push('regime_not_yet_improved');
    const state=blockers.length?'BLOCKED':flags.length?'WATCH':'PASS';
    const mode=cause===CAUSES.EVENT?'EVENT':([CAUSES.MARKET,CAUSES.SECTOR].includes(cause)?'REGIME_SHIFT':'NORMAL');
    return result('ReentryGate',{state,mode,cause,thesisState:thesis||null,blockers,flags,freshEntryGateState:fresh?normState(fresh.state):null,note:'Re-entry is a new decision. Old Company Quality cannot shortcut the fresh Entry Gate.'});
  }

  function rotationAlpha(input={}){
    const sold=input.soldAsset||{}; const repl=input.replacement||{};
    if(!num(sold.returnPct)||!num(repl.returnPct)) return result('RotationAlpha',{state:'OPEN',alphaPct:null,note:'Both same-period returns are required.'});
    const samePeriod=(!sold.startDate||!repl.startDate||sold.startDate===repl.startDate)&&(!sold.endDate||!repl.endDate||sold.endDate===repl.endDate);
    if(!samePeriod) return result('RotationAlpha',{state:'OPEN',alphaPct:null,note:'Returns must use the same start/end period.'});
    const alpha=round(repl.returnPct-sold.returnPct);
    return result('RotationAlpha',{state:alpha>1?'POSITIVE':alpha<-1?'NEGATIVE':'FLAT',alphaPct:alpha,soldTicker:sold.ticker||null,replacementTicker:repl.ticker||null,soldReturnPct:sold.returnPct,replacementReturnPct:repl.returnPct,note:'Measures rotation quality, not regret.'});
  }

  function portfolioRiskMap(positions=[], options={}){
    const rows=(Array.isArray(positions)?positions:[]).map(p=>({
      ticker:p.ticker||null,
      sleeve:Object.values(SLEEVES).includes(normState(p.sleeve))?normState(p.sleeve):SLEEVES.CORE,
      weightPct:num(p.weightPct)?p.weightPct:(num(p.weight)?p.weight*100:null),
      sector:p.sector||'UNKNOWN', debtRisk:num(p.debtRisk)?clamp(p.debtRisk):null,
      eventRisk:num(p.eventRisk)?clamp(p.eventRisk):null, geoRisk:num(p.geoRisk)?clamp(p.geoRisk):null
    }));
    const maxLimit=num(options.maxSinglePositionPct)?options.maxSinglePositionPct:CONFIG.maxSinglePositionPct;
    const maxRow=rows.filter(r=>num(r.weightPct)).sort((a,b)=>b.weightPct-a.weightPct)[0]||null;
    const sleeveExposure=Object.fromEntries(Object.values(SLEEVES).map(s=>[s,round(rows.filter(r=>r.sleeve===s&&num(r.weightPct)).reduce((a,r)=>a+r.weightPct,0))]));
    const sectors={}; rows.forEach(r=>{if(num(r.weightPct)) sectors[r.sector]=(sectors[r.sector]||0)+r.weightPct});
    const topSector=Object.entries(sectors).sort((a,b)=>b[1]-a[1])[0]||null;
    const weightedRisk=(key)=>{
      const valid=rows.filter(r=>num(r.weightPct)&&num(r[key])); const sw=valid.reduce((a,r)=>a+r.weightPct,0);
      return sw?round(valid.reduce((a,r)=>a+r.weightPct*r[key],0)/sw):null;
    };
    return result('PortfolioRiskMap',{maxSinglePosition:maxRow,maxSinglePositionLimitPct:maxLimit,maxSinglePositionWarning:!!(maxRow&&maxRow.weightPct>maxLimit),topSector:topSector?{sector:topSector[0],weightPct:round(topSector[1])}:null,sleeveExposure,risk:{debt:weightedRisk('debtRisk'),event:weightedRisk('eventRisk'),geopolitical:weightedRisk('geoRisk')},rows,note:'CORE / TACTICAL / SATELLITE exposures remain separate.'});
  }

  function positionBuilder(input={}){
    const blockers=[];
    if(!gatePass(input.entryGate)) blockers.push('entry_gate_not_passed');
    if(!num(input.maxPositionPct)) blockers.push('max_position_missing');
    if(!input.thesisBreakLevel) blockers.push('thesis_break_level_missing');
    if(blockers.length) return result('PositionBuilder',{state:'BLOCKED',blockers,starterPct:null,addZones:[],note:'Entry Gate comes before capital.'});
    const max=input.maxPositionPct;
    const starter=num(input.starterPct)?Math.min(input.starterPct,max):round(max*0.35);
    const zones=Array.isArray(input.addZones)?input.addZones:[];
    return result('PositionBuilder',{state:'PLAN_READY',starterPct:round(starter),maxPositionPct:max,addZones:zones,thesisBreakLevel:input.thesisBreakLevel,note:'Sizing scaffold only; thresholds remain user/config driven.'});
  }

  function evaluatePortfolio(snapshot={}){
    const drag=portfolioDrag(snapshot.positions||[]);
    const risk=portfolioRiskMap(snapshot.positions||[],snapshot.riskOptions||{});
    const comp=capitalCompetition(snapshot.currentHolding||{},snapshot.alternatives||[]);
    const ex=exitScore(snapshot.exitRisk||{});
    const challenge=exitChallenge({...snapshot.exitContext,exitRisk:ex.risk});
    const cause=causeOfMove(snapshot.causeSignals||{});
    const rg=reentryGate({...snapshot.reentry,causeOfMove:snapshot.reentry?.causeOfMove||cause});
    const rot=rotationAlpha(snapshot.rotation||{});
    return result('PortfolioSystem',{drag,riskMap:risk,capitalCompetition:comp,exitScore:ex,exitChallenge:challenge,causeOfMove:cause,reentryGate:rg,rotationAlpha:rot,note:'Separate motors; no universal portfolio action score.'});
  }

  function selfTest(){
    const passGate={state:'PASS'}; const blockGate={state:'BLOCKED'};
    const sale=makeSaleRecord({ticker:'AAA',date:'2026-08-01',salePrice:100,reason:'valuation reset',thesisStateAtSale:'INTACT'});
    const p1=decisionProtection(ACTIONS.SELL,{reason:'THESIS_BREAK',thesisState:'BROKEN',purchasePrice:500});
    const p2=decisionProtection(ACTIONS.SELL,{reason:'THESIS_BREAK',thesisState:'BROKEN',purchasePrice:5});
    const history=[deepFreeze({id:'old',action:'BUY'})]; const h2=appendDecisionJournal(history,{ticker:'AAA',action:'SELL',thesisSnapshot:'test'});
    const highCQBlocked=reentryGate({soldRecord:sale,causeOfMove:CAUSES.VALUATION,freshEntryGate:blockGate,thesisState:'INTACT',verificationStatus:'VERIFIED',companyQuality:10});
    const lowCQBlocked=reentryGate({soldRecord:sale,causeOfMove:CAUSES.VALUATION,freshEntryGate:blockGate,thesisState:'INTACT',verificationStatus:'VERIFIED',companyQuality:1});
    const companyBlock=reentryGate({soldRecord:sale,causeOfMove:CAUSES.COMPANY,freshEntryGate:passGate,thesisState:'BROKEN',verificationStatus:'VERIFIED'});
    const companyRepair=reentryGate({soldRecord:sale,causeOfMove:CAUSES.COMPANY,freshEntryGate:passGate,thesisState:'REPAIRED',verificationStatus:'VERIFIED'});
    const valuePass=reentryGate({soldRecord:sale,causeOfMove:CAUSES.VALUATION,freshEntryGate:passGate,thesisState:'INTACT',verificationStatus:'VERIFIED'});
    const rot=rotationAlpha({soldAsset:{ticker:'AAA',returnPct:2,startDate:'2026-08-01',endDate:'2026-08-29'},replacement:{ticker:'BBB',returnPct:7,startDate:'2026-08-01',endDate:'2026-08-29'}});
    const rotBad=rotationAlpha({soldAsset:{returnPct:2,startDate:'A',endDate:'B'},replacement:{returnPct:7,startDate:'C',endDate:'B'}});
    const drag=portfolioDrag([{ticker:'A',weight:.5,returnPct:-10,thesisHealth:9},{ticker:'B',weight:.5,returnPct:3,thesisHealth:3}]);
    const comp=capitalCompetition({ticker:'A',opportunityScore:5,riskScore:5},[{ticker:'B',opportunityScore:9,riskScore:3}]);
    const risk=portfolioRiskMap([{ticker:'A',weightPct:25,sleeve:'CORE',sector:'AI',debtRisk:2,eventRisk:4,geoRisk:3},{ticker:'B',weightPct:10,sleeve:'SATELLITE',sector:'AI',debtRisk:8,eventRisk:9,geoRisk:7}]);
    const pb=positionBuilder({entryGate:blockGate,maxPositionPct:10,thesisBreakLevel:'X'});
    const causeUnknown=causeOfMove({companyDamage:2,marketShock:3});
    const assertions=[
      ['purchase price anchoring does not change Decision Protection',JSON.stringify(p1.checks)===JSON.stringify(p2.checks)&&p1.state===p2.state],
      ['Decision Journal is append-only',history.length===1&&h2.length===2&&h2[0].id==='old'],
      ['Decision Journal stamps model version',h2[1].modelVersion===VERSION],
      ['sale record is immutable',Object.isFrozen(sale)&&sale.immutable===true],
      ['fresh Entry Gate blocks re-entry even with high Company Quality',highCQBlocked.state==='BLOCKED'],
      ['Company Quality cannot shortcut Re-entry Gate',highCQBlocked.state===lowCQBlocked.state],
      ['company damage blocks unrepaired thesis',companyBlock.state==='BLOCKED'],
      ['company damage can be reconsidered after thesis repair + fresh gate',companyRepair.state==='PASS'],
      ['valuation reset can pass with intact thesis + fresh gate',valuePass.state==='PASS'],
      ['Rotation Alpha compares same-period replacement vs sold asset',rot.alphaPct===5&&rot.state==='POSITIVE'],
      ['Rotation Alpha refuses mismatched periods',rotBad.state==='OPEN'],
      ['Portfolio Drag separates price pain from thesis pain',drag.pricePain!==drag.thesisPain&&drag.priceDetractors[0].ticker==='A'&&drag.thesisDetractors[0].ticker==='B'],
      ['Capital Competition creates review, not trade instruction',comp.state==='REVIEW'&&!('sell' in comp)&&!('buy' in comp)],
      ['Portfolio Risk Map keeps sleeves separate',risk.sleeveExposure.CORE===25&&risk.sleeveExposure.SATELLITE===10],
      ['max single position warning fires',risk.maxSinglePositionWarning===true],
      ['Position Builder cannot bypass Entry Gate',pb.state==='BLOCKED'],
      ['Cause-of-Move remains UNKNOWN when evidence is weak',causeUnknown.cause===CAUSES.UNKNOWN],
      ['Exit Score and Exit Challenge remain separate engines',exitScore({overvaluationRisk:8}).engine==='ExitScore'&&exitChallenge({reason:'THESIS_BREAK'}).engine==='ExitChallenge']
    ];
    return deepFreeze({version:VERSION,passed:assertions.every(([,ok])=>!!ok),tests:assertions.map(([name,ok])=>({name,passed:!!ok}))});
  }

  const DEMO=deepFreeze({
    positions:[
      {ticker:'NVDA',weightPct:18,sleeve:'CORE',sector:'AI Infrastructure',returnPct:-4,thesisHealth:8.6,debtRisk:2,eventRisk:6,geoRisk:5},
      {ticker:'DEMO-B',weightPct:8,sleeve:'TACTICAL',sector:'Power & Grid',returnPct:6,thesisHealth:7.4,debtRisk:4,eventRisk:4,geoRisk:2},
      {ticker:'DEMO-C',weightPct:4,sleeve:'SATELLITE',sector:'Quantum',returnPct:-12,thesisHealth:4.5,debtRisk:7,eventRisk:8,geoRisk:3}
    ],
    currentHolding:{ticker:'NVDA',opportunityScore:6.4,riskScore:5.0},
    alternatives:[{ticker:'DEMO-B',opportunityScore:7.8,riskScore:4.0},{ticker:'DEMO-C',opportunityScore:8.4,riskScore:8.0}],
    exitRisk:{overvaluationRisk:7.5,sectorOutflowRisk:2.5,momentumRisk:4.5,expectationRisk:7.8,eventRisk:5.5,thesisDamageRisk:2.5},
    exitContext:{reason:'REVIEW',fundamentalsTrend:'IMPROVING',regimeTrend:'SUPPORTIVE',thesisState:'INTACT'},
    causeSignals:{companyDamage:2.5,sectorShock:3,marketShock:4.5,eventShock:3,expectationsReset:7.2,valuationReset:7.8},
    reentry:{soldRecord:makeSaleRecord({ticker:'NVDA',date:'DEMO',salePrice:178,reason:'valuation reset',thesisStateAtSale:'INTACT'}),freshEntryGate:{state:'BLOCKED'},thesisState:'INTACT',verificationStatus:'UNVERIFIED'},
    rotation:{soldAsset:{ticker:'OLD',returnPct:-2,startDate:'DEMO-A',endDate:'DEMO-B'},replacement:{ticker:'NEW',returnPct:4,startDate:'DEMO-A',endDate:'DEMO-B'}}
  });

  const API={VERSION,CALIBRATED,ACTIONS,CAUSES,SLEEVES,CONFIG,decisionProtection,appendDecisionJournal,makeSaleRecord,thesisCard,capitalCompetition,portfolioDrag,exitScore,exitChallenge,causeOfMove,seedReentryWatch,reentryGate,rotationAlpha,portfolioRiskMap,positionBuilder,evaluatePortfolio,selfTest,DEMO};
  try{ API.demoResult=evaluatePortfolio(DEMO); API.selfTestResult=selfTest(); }catch(e){ API.bootstrapError=String(e&&e.message||e); }
  return Object.freeze(API);
});

